/**
 * Voice AI — Agent Voice Tab
 * Basic info, personality, and TTS voice selection with filterable voice grid.
 * Provider-aware: detects active TTS provider (Deepgram/ElevenLabs) and fetches voices accordingly.
 * Dispatches: save, agent-changed
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { authService } from '../../services/auth-service.js';

const API_BASE = '/voice/php/api';

const VOICE_SETTINGS_DEFAULTS = {
  stability: 0.5,
  similarity_boost: 0.5,
  style: 0,
};

export class AgentVoiceTab extends LitElement {
  static properties = {
    agent: { type: Object },
    saving: { type: Boolean },
    ttsProviders: { type: Array },
    _voices: { type: Array, state: true },
    _filteredVoices: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _activeProvider: { type: String, state: true },
    _playingVoice: { type: String, state: true },
    _loadingPreview: { type: String, state: true },
    _genderFilters: { type: Array, state: true },
    _accentFilter: { type: String, state: true },
    _useCaseFilter: { type: String, state: true },
    _ageFilter: { type: String, state: true },
    _visibleCount: { type: Number, state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles];

  constructor() {
    super();
    this.ttsProviders = [];
    this._voices = [];
    this._filteredVoices = [];
    this._loading = false;
    this._error = '';
    this._activeProvider = '';
    this._playingVoice = null;
    this._loadingPreview = null;
    this._genderFilters = [];
    this._accentFilter = '';
    this._useCaseFilter = '';
    this._ageFilter = '';
    this._visibleCount = 12;
    this._audio = null;
    this._lastAgentId = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadVoices();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopAudio();
  }

  updated(changed) {
    if (changed.has('agent') && this.agent?.id !== this._lastAgentId) {
      this._lastAgentId = this.agent?.id;
    }
    // Re-load voices when TTS providers list arrives or changes
    if (changed.has('ttsProviders') && this.ttsProviders?.length) {
      const newProvider = this._detectProvider();
      // Load if provider changed, or if voices haven't loaded yet (initial async timing)
      if (newProvider !== this._activeProvider || !this._voices.length) {
        this._loadVoices();
      }
    }
  }

  /**
   * Detect the active TTS provider for this agent.
   * Agent override > global default > first available.
   */
  _detectProvider() {
    // Check agent-specific TTS provider override
    const agentKey = this.agent?.ttsProviderKey;
    if (agentKey && this.ttsProviders?.length) {
      const match = this.ttsProviders.find(p => p.key === agentKey);
      if (match) return match.provider || 'deepgram';
    }
    // Global default (first provider marked is_default, or first in list)
    if (this.ttsProviders?.length) {
      const def = this.ttsProviders.find(p => p.is_default) || this.ttsProviders[0];
      return def.provider || 'deepgram';
    }
    return ''; // Let server auto-detect
  }

  async _loadVoices() {
    if (this._loading) return;
    this._loading = true;
    this._error = '';
    try {
      const provider = this._detectProvider();
      this._activeProvider = provider;
      const providerParam = provider ? `?provider=${provider}` : '';
      const res = await authService.apiFetch(`${API_BASE}/deepgram-voices.php${providerParam}`);
      const data = await res.json();
      if (data.success) {
        this._activeProvider = data.provider || provider;
        this._voices = data.voices || [];
        this._applyFilters();
      } else {
        this._error = data.error || 'Failed to load voices';
      }
    } catch (err) {
      this._error = err.message;
    } finally {
      this._loading = false;
    }
  }

  // ── Filters ──

  _toggleGender(gender) {
    const idx = this._genderFilters.indexOf(gender);
    if (idx >= 0) {
      this._genderFilters = this._genderFilters.filter(g => g !== gender);
    } else {
      this._genderFilters = [...this._genderFilters, gender];
    }
    this._applyFilters();
  }

  _setAccent(e) {
    this._accentFilter = e.target.value;
    this._applyFilters();
  }

  _setUseCase(e) {
    this._useCaseFilter = e.target.value;
    this._applyFilters();
  }

  _setAge(e) {
    this._ageFilter = e.target.value;
    this._applyFilters();
  }

  _clearFilters() {
    this._genderFilters = [];
    this._accentFilter = '';
    this._useCaseFilter = '';
    this._ageFilter = '';
    this._applyFilters();
  }

  get _hasFilters() {
    return this._genderFilters.length > 0 || this._accentFilter || this._useCaseFilter || this._ageFilter;
  }

  _applyFilters() {
    let filtered = [...this._voices];

    if (this._genderFilters.length > 0) {
      filtered = filtered.filter(v => this._genderFilters.includes(v.gender));
    }
    if (this._accentFilter) {
      filtered = filtered.filter(v => v.accent === this._accentFilter);
    }
    if (this._useCaseFilter) {
      filtered = filtered.filter(v =>
        (v.use_cases || []).some(uc => uc.toLowerCase() === this._useCaseFilter.toLowerCase())
      );
    }
    if (this._ageFilter) {
      filtered = filtered.filter(v => v.age === this._ageFilter);
    }

    this._filteredVoices = filtered;
    this._visibleCount = 12;
  }

  // ── Unique filter values ──

  get _accents() {
    const set = new Set(this._voices.map(v => v.accent).filter(Boolean));
    return [...set].sort();
  }

  get _useCases() {
    const set = new Set();
    this._voices.forEach(v => (v.use_cases || []).forEach(uc => set.add(uc)));
    return [...set].sort();
  }

  get _ages() {
    const set = new Set(this._voices.map(v => v.age).filter(Boolean));
    return [...set].sort();
  }

  // ── Audio Preview ──

  async _togglePlay(voice) {
    if (this._playingVoice === voice.canonical_name) {
      this._stopAudio();
      return;
    }
    this._stopAudio();

    let audioSrc = voice.sample;

    // Cartesia voices have no static sample — generate on-demand via server
    if (!audioSrc && this._activeProvider === 'cartesia' && voice.canonical_name) {
      this._loadingPreview = voice.canonical_name;
      this._playingVoice = voice.canonical_name;
      try {
        const res = await authService.apiFetch(
          `${API_BASE}/voice-preview.php?provider=cartesia&voice_id=${encodeURIComponent(voice.canonical_name)}`
        );
        if (!res.ok) { this._loadingPreview = null; this._playingVoice = null; return; }
        const blob = await res.blob();
        audioSrc = URL.createObjectURL(blob);
      } catch {
        this._loadingPreview = null;
        this._playingVoice = null;
        return;
      }
      this._loadingPreview = null;
    }

    if (!audioSrc) return;

    this._audio = new Audio(audioSrc);
    this._playingVoice = voice.canonical_name;
    this._audio.addEventListener('ended', () => {
      this._playingVoice = null;
    });
    this._audio.addEventListener('error', () => {
      this._playingVoice = null;
    });
    this._audio.play().catch(() => { this._playingVoice = null; });
  }

  _stopAudio() {
    if (this._audio) {
      // Revoke blob URLs to prevent memory leaks
      if (this._audio.src && this._audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(this._audio.src);
      }
      this._audio.pause();
      this._audio.src = '';
      this._audio = null;
    }
    this._playingVoice = null;
    this._loadingPreview = null;
  }

  // ── Voice Status Display ──

  _renderVoiceStatus(agent) {
    if (!agent.voiceName) {
      return html`<span style="margin-left:var(--space-2);color:var(--color-text-tertiary);font-style:italic;">Using Settings Default</span>`;
    }
    // Check if the saved voice matches any voice in the current provider's list
    const matchingVoice = this._voices.find(v => v.canonical_name === agent.voiceName);
    if (matchingVoice) {
      return html`<span style="margin-left:var(--space-2);font-family:var(--font-mono);color:var(--color-primary);">${matchingVoice.name} (${agent.voiceName})</span>`;
    }
    // Voice doesn't match current provider — show mismatch warning
    if (this._voices.length > 0) {
      return html`
        <span style="margin-left:var(--space-2);color:var(--color-warning);font-size:var(--text-xs);">
          <i class="fa-sharp fa-light fa-triangle-exclamation" style="margin-right:2px;"></i>
          "${agent.voiceName}" doesn't match current ${this._activeProvider} provider — select a new voice below
        </span>
      `;
    }
    // Voices still loading
    return html`<span style="margin-left:var(--space-2);font-family:var(--font-mono);color:var(--color-primary);">${agent.voiceName}</span>`;
  }

  // ── Voice Selection ──

  _selectVoice(voice) {
    const updated = { ...this.agent, voiceName: voice.canonical_name };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
  }

  // ── Voice Settings (ElevenLabs only) ──

  _getVoiceSetting(key) {
    return this.agent?.voiceSettings?.[key] ?? VOICE_SETTINGS_DEFAULTS[key];
  }

  _setVoiceSetting(key, value) {
    const updated = { ...this.agent };
    const settings = { ...(updated.voiceSettings || {}) };
    settings[key] = value;
    updated.voiceSettings = settings;
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
  }

  _resetVoiceSettings() {
    const updated = { ...this.agent, voiceSettings: null };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
  }

  get _hasCustomVoiceSettings() {
    return this.agent?.voiceSettings != null && Object.keys(this.agent.voiceSettings).length > 0;
  }

  // ── Save ──

  // ── TTS Word Replacements ──

  _toggleWordReplacement(index, enabled) {
    const list = [...(this.agent.ttsWordReplacements || [])];
    list[index] = { ...list[index], enabled };
    const updated = { ...this.agent, ttsWordReplacements: list };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
  }

  _updateWordReplacement(index, field, value) {
    const list = [...(this.agent.ttsWordReplacements || [])];
    list[index] = { ...list[index], [field]: value };
    const updated = { ...this.agent, ttsWordReplacements: list };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
  }

  _removeWordReplacement(index) {
    const list = [...(this.agent.ttsWordReplacements || [])];
    list.splice(index, 1);
    const updated = { ...this.agent, ttsWordReplacements: list };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
  }

  _addWordReplacement() {
    const list = [...(this.agent.ttsWordReplacements || []), { from: '', to: '', enabled: true, note: '' }];
    const updated = { ...this.agent, ttsWordReplacements: list };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
  }

  _collectAndSave() {
    const root = this.shadowRoot;
    const field = (name) => {
      const el = root.querySelector(`[data-field="${name}"]`);
      return el ? el.value : '';
    };

    const updated = { ...this.agent };
    updated.name = field('name');
    updated.description = field('description');
    updated.language = field('language');
    updated.systemPrompt = field('systemPrompt');
    updated.tone = field('tone');
    updated.greeting = field('greeting');
    updated.greetingUseName = root.querySelector('[data-field="greetingUseName"]')?.checked ? 1 : 0;
    updated.greetingLlmFreestyle = root.querySelector('[data-field="greetingLlmFreestyle"]')?.checked ? 1 : 0;
    updated.afterHoursMessage = field('afterHoursMessage');

    // Merge persona fields into voiceSettings
    const vs = { ...(updated.voiceSettings || {}) };
    vs.persona_name = field('persona_name');
    vs.role = field('role');
    vs.about = field('about');
    updated.voiceSettings = vs;

    this.dispatchEvent(new CustomEvent('save', { detail: { agent: updated } }));
  }

  // ── Render ──

  render() {
    const agent = this.agent;
    if (!agent) return html``;

    return html`
      ${iconStyles}

      <!-- Basic Information -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-id-card" style="margin-right:var(--space-2);color:var(--color-primary);"></i>
          Basic Information
        </div>
        <div class="config-section-desc">Core details and identity for this agent.</div>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Agent Name</label>
            <input type="text" class="form-input" data-field="name" .value=${agent.name} />
          </div>
          <div class="form-group">
            <label class="form-label">Language</label>
            <select class="form-select" data-field="language">
              <option value="en-US" ?selected=${agent.language === 'en-US'}>English (US)</option>
              <option value="en-GB" ?selected=${agent.language === 'en-GB'}>English (UK)</option>
              <option value="es-MX" ?selected=${agent.language === 'es-MX'}>Spanish (MX)</option>
              <option value="fr-FR">French</option>
            </select>
          </div>
        </div>
        <div class="form-grid full" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Description</label>
            <textarea class="form-textarea" data-field="description" style="min-height:70px;" .value=${agent.description}></textarea>
          </div>
        </div>
      </div>

      <!-- Agent Persona -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-user-tie" style="margin-right:var(--space-2);color:var(--color-success);"></i>
          Agent Persona
        </div>
        <div class="config-section-desc">Give this agent a human identity. Callers who ask "What's your name?" or "Who am I speaking with?" will get a natural answer. Business details are pulled automatically from your system config.</div>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Persona Name</label>
            <input type="text" class="form-input" data-field="persona_name"
              placeholder="e.g. Sarah"
              .value=${agent.voiceSettings?.persona_name || ''} />
            <div class="form-hint">The first name callers will hear when they ask who they're speaking with.</div>
          </div>
          <div class="form-group">
            <label class="form-label">Role / Title</label>
            <input type="text" class="form-input" data-field="role"
              placeholder="e.g. Front Desk Receptionist"
              .value=${agent.voiceSettings?.role || ''} />
            <div class="form-hint">Job title the agent uses to describe itself.</div>
          </div>
        </div>
        <div class="form-grid full" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">About</label>
            <textarea class="form-textarea" data-field="about"
              style="min-height:70px;"
              placeholder="e.g. I love helping guests find the perfect spot for their trip. I know every corner of this park!"
              .value=${agent.voiceSettings?.about || ''}></textarea>
            <div class="form-hint">Short personality background — gives the AI a natural, human feel when chatting with callers.</div>
          </div>
        </div>
      </div>

      <!-- Personality & Behavior -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-brain" style="margin-right:var(--space-2);color:var(--color-info);"></i>
          Personality & Behavior
        </div>
        <div class="config-section-desc">Define how the agent speaks and behaves on calls. Identity (name, role, location) is set in the Persona section above — don't repeat it here.</div>

        <div class="form-grid full">
          <div class="form-group">
            <label class="form-label">Behavior Prompt</label>
            <textarea
              class="form-textarea"
              data-field="systemPrompt"
              style="min-height:120px;font-family:var(--font-mono);font-size:var(--text-xs);"
              placeholder="Describe speaking style, personality traits, rules, and behavior. Leave blank to use the default conversational style."
              .value=${agent.systemPrompt}
            ></textarea>
            <div class="char-count">${(agent.systemPrompt || '').length} / 4000 characters</div>
          </div>
        </div>
        <div class="form-grid" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Tone</label>
            <select class="form-select" data-field="tone">
              <option value="professional" ?selected=${agent.tone === 'professional'}>Professional</option>
              <option value="friendly" ?selected=${agent.tone === 'friendly'}>Friendly</option>
              <option value="casual" ?selected=${agent.tone === 'casual'}>Casual</option>
              <option value="formal" ?selected=${agent.tone === 'formal'}>Formal</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Greeting Message</label>
            <input type="text" class="form-input" data-field="greeting" .value=${agent.greeting}
              ?disabled=${agent.greetingLlmFreestyle} />
            <div style="display:flex;gap:var(--space-6);margin-top:var(--space-2);">
              <label style="display:flex;align-items:center;gap:var(--space-2);font-size:var(--text-xs);color:var(--color-text-secondary);cursor:pointer;">
                <input type="checkbox" data-field="greetingUseName"
                  ?checked=${agent.greetingUseName !== false && agent.greetingUseName !== 0}
                  ?disabled=${agent.greetingLlmFreestyle} />
                Include caller's name
              </label>
              <label style="display:flex;align-items:center;gap:var(--space-2);font-size:var(--text-xs);color:var(--color-text-secondary);cursor:pointer;">
                <input type="checkbox" data-field="greetingLlmFreestyle"
                  ?checked=${agent.greetingLlmFreestyle}
                  @change=${e => {
                    const updated = { ...this.agent, greetingLlmFreestyle: e.target.checked ? 1 : 0 };
                    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
                  }} />
                Let AI choose its own greeting each call
              </label>
            </div>
          </div>
        </div>
        <div class="form-grid full" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">After-Hours Message</label>
            <input type="text" class="form-input" data-field="afterHoursMessage" .value=${agent.afterHoursMessage} placeholder="Message when calling outside business hours" />
          </div>
        </div>
      </div>

      <!-- TTS Word Replacements -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-waveform-lines" style="margin-right:6px;"></i>
          TTS Pronunciation Fixes
        </div>
        <p style="font-size:var(--text-xs);color:var(--color-text-secondary);margin:0 0 var(--space-3);">
          Words that the selected voice mispronounces. Add replacements — the original word is swapped before sending to TTS.
        </p>
        <div style="display:flex;flex-direction:column;gap:var(--space-2);">
          ${(agent.ttsWordReplacements || []).map((r, i) => html`
            <div style="display:flex;align-items:center;gap:var(--space-2);padding:var(--space-2);background:var(--color-bg-subtle);border-radius:6px;">
              <label style="display:flex;align-items:center;gap:var(--space-1);cursor:pointer;">
                <input type="checkbox" ?checked=${r.enabled !== false}
                  @change=${e => this._toggleWordReplacement(i, e.target.checked)} />
              </label>
              <input type="text" class="form-input" style="flex:1;min-width:80px;padding:4px 8px;font-size:var(--text-xs);"
                .value=${r.from || ''} placeholder="Original word"
                @change=${e => this._updateWordReplacement(i, 'from', e.target.value)} />
              <span style="color:var(--color-text-secondary);font-size:var(--text-xs);">→</span>
              <input type="text" class="form-input" style="flex:1;min-width:80px;padding:4px 8px;font-size:var(--text-xs);"
                .value=${r.to || ''} placeholder="Say instead"
                @change=${e => this._updateWordReplacement(i, 'to', e.target.value)} />
              <span style="font-size:var(--text-xs);color:var(--color-text-tertiary);flex:1;min-width:60px;">${r.note || ''}</span>
              <button class="btn btn-ghost" style="padding:2px 6px;font-size:var(--text-xs);color:var(--color-danger);"
                @click=${() => this._removeWordReplacement(i)}>
                <i class="fa-sharp fa-light fa-trash"></i>
              </button>
            </div>
          `)}
          <button class="btn btn-default" style="align-self:flex-start;font-size:var(--text-xs);padding:4px 10px;"
            @click=${this._addWordReplacement}>
            <i class="fa-sharp fa-light fa-plus" style="margin-right:4px;"></i> Add Replacement
          </button>
        </div>
      </div>

      <!-- Voice Selection -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-waveform-lines" style="margin-right:var(--space-2);color:var(--color-accent);"></i>
          Voice Selection
        </div>
        <div class="config-section-desc">
          Choose a voice for this agent${this._activeProvider ? html` <span style="font-weight:var(--font-semibold);text-transform:capitalize;">(${this._activeProvider})</span>` : ''}. Click to select, use the play button to preview.
          ${this._renderVoiceStatus(agent)}
        </div>

        ${this._renderFilterBar()}
        ${this._loading ? this._renderVoiceSkeleton() : ''}
        ${this._error ? html`<div class="voice-error"><i class="fa-sharp fa-light fa-triangle-exclamation"></i> ${this._error}</div>` : ''}
        ${!this._loading && !this._error ? this._renderVoiceGrid() : ''}
      </div>

      ${this._renderVoiceSettings()}

      <div class="actions-footer">
        <div></div>
        <div style="display:flex;gap:var(--space-3);">
          <button class="btn btn-primary" ?disabled=${this.saving} @click=${() => this._collectAndSave()}>
            <i class="fa-sharp fa-light ${this.saving ? 'fa-spinner-third fa-spin' : 'fa-floppy-disk'}"></i>
            ${this.saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    `;
  }

  _renderVoiceSettings() {
    if (this._activeProvider !== 'elevenlabs') return '';

    const stability = this._getVoiceSetting('stability');
    const similarityBoost = this._getVoiceSetting('similarity_boost');
    const style = this._getVoiceSetting('style');

    return html`
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-sliders" style="margin-right:var(--space-2);color:var(--color-warning);"></i>
          Voice Settings
          ${this._hasCustomVoiceSettings ? html`
            <button class="btn btn-ghost btn-sm" style="margin-left:auto;font-size:var(--text-xs);"
              @click=${this._resetVoiceSettings}>
              <i class="fa-sharp fa-light fa-rotate-left"></i> Reset
            </button>
          ` : ''}
        </div>
        <div class="config-section-desc">
          Fine-tune the ElevenLabs voice characteristics for this agent.
          ${!this._hasCustomVoiceSettings ? html`<span style="color:var(--color-text-tertiary);font-style:italic;"> Using provider defaults.</span>` : ''}
        </div>

        <div class="slider-group">
          <div class="slider-header">
            <label class="form-label">Stability</label>
            <span class="slider-value">${Number(stability).toFixed(2)}</span>
          </div>
          <input type="range" min="0" max="1" step="0.05"
            .value=${String(stability)}
            @input=${e => this._setVoiceSetting('stability', parseFloat(e.target.value))}
          />
          <div class="form-hint">Lower = more expressive and emotional. Higher = more consistent and monotonous.</div>
        </div>

        <div class="slider-group" style="margin-top:var(--space-4);">
          <div class="slider-header">
            <label class="form-label">Similarity Boost</label>
            <span class="slider-value">${Number(similarityBoost).toFixed(2)}</span>
          </div>
          <input type="range" min="0" max="1" step="0.05"
            .value=${String(similarityBoost)}
            @input=${e => this._setVoiceSetting('similarity_boost', parseFloat(e.target.value))}
          />
          <div class="form-hint">How closely the AI adheres to the original voice. Higher values sound more like the selected voice.</div>
        </div>

        <div class="slider-group" style="margin-top:var(--space-4);">
          <div class="slider-header">
            <label class="form-label">Style Exaggeration</label>
            <span class="slider-value">${Number(style).toFixed(2)}</span>
          </div>
          <input type="range" min="0" max="1" step="0.05"
            .value=${String(style)}
            @input=${e => this._setVoiceSetting('style', parseFloat(e.target.value))}
          />
          <div class="form-hint" style="color:var(--color-warning);">
            <i class="fa-sharp fa-light fa-triangle-exclamation" style="margin-right:var(--space-1);"></i>
            Amplifies the speaker's style. Higher values increase latency. Recommended: keep at 0 for phone calls.
          </div>
        </div>
      </div>
    `;
  }

  _renderFilterBar() {
    return html`
      <div class="voice-filter-bar">
        <label class="voice-gender-check" @click=${() => this._toggleGender('feminine')}>
          <span class="voice-checkbox ${this._genderFilters.includes('feminine') ? 'checked' : ''}">
            ${this._genderFilters.includes('feminine') ? html`<i class="fa-sharp fa-solid fa-check"></i>` : ''}
          </span>
          Feminine
        </label>
        <label class="voice-gender-check" @click=${() => this._toggleGender('masculine')}>
          <span class="voice-checkbox ${this._genderFilters.includes('masculine') ? 'checked' : ''}">
            ${this._genderFilters.includes('masculine') ? html`<i class="fa-sharp fa-solid fa-check"></i>` : ''}
          </span>
          Masculine
        </label>

        <select class="form-select" @change=${this._setAccent} .value=${this._accentFilter}>
          <option value="">All Accents</option>
          ${this._accents.map(a => html`<option value=${a} ?selected=${this._accentFilter === a}>${a}</option>`)}
        </select>

        <select class="form-select" @change=${this._setAge} .value=${this._ageFilter}>
          <option value="">All Ages</option>
          ${this._ages.map(a => html`<option value=${a} ?selected=${this._ageFilter === a}>${a}</option>`)}
        </select>

        <select class="form-select" @change=${this._setUseCase} .value=${this._useCaseFilter}>
          <option value="">All Use Cases</option>
          ${this._useCases.map(uc => html`<option value=${uc} ?selected=${this._useCaseFilter === uc}>${uc}</option>`)}
        </select>

        ${this._hasFilters ? html`
          <button class="voice-clear-btn" @click=${this._clearFilters}>
            <i class="fa-sharp fa-light fa-xmark"></i> Clear
          </button>
        ` : ''}

        <span class="voice-count">${this._filteredVoices.length} voice${this._filteredVoices.length !== 1 ? 's' : ''}</span>
      </div>
    `;
  }

  get _batchSize() { return 12; }

  get _visibleVoices() {
    return this._filteredVoices.slice(0, this._visibleCount);
  }

  get _hasMore() {
    return this._visibleCount < this._filteredVoices.length;
  }

  _loadMore() {
    this._visibleCount = Math.min(this._visibleCount + this._batchSize, this._filteredVoices.length);
  }

  _renderVoiceGrid() {
    if (this._filteredVoices.length === 0) {
      return html`
        <div class="voice-empty">
          <i class="fa-sharp fa-light fa-microphone-slash" style="font-size:var(--text-2xl);color:var(--color-text-disabled);margin-bottom:var(--space-2);"></i>
          <div>No voices match your filters</div>
          ${this._hasFilters ? html`<button class="btn btn-ghost btn-sm" @click=${this._clearFilters}>Clear Filters</button>` : ''}
        </div>
      `;
    }

    const visible = this._visibleVoices;
    const total = this._filteredVoices.length;

    return html`
      <div class="voice-grid">
        ${visible.map(voice => this._renderVoiceCard(voice))}
      </div>
      ${this._hasMore ? html`
        <div class="voice-load-more">
          <button class="btn btn-secondary voice-load-more-btn" @click=${() => this._loadMore()}>
            <i class="fa-sharp fa-light fa-chevron-down"></i>
            Load More Voices
          </button>
          <span class="voice-load-more-info">Showing ${visible.length} of ${total}</span>
        </div>
      ` : total > this._batchSize ? html`
        <div class="voice-load-more">
          <span class="voice-load-more-info">Showing all ${total} voices</span>
        </div>
      ` : ''}
    `;
  }

  _renderVoiceCard(voice) {
    const isSelected = (this.agent.voiceName || '') === voice.canonical_name;
    const isPlaying = this._playingVoice === voice.canonical_name;
    const isLoading = this._loadingPreview === voice.canonical_name;

    return html`
      <div
        class="voice-card ${isSelected ? 'selected' : ''} ${isPlaying ? 'playing' : ''}"
        @click=${() => this._selectVoice(voice)}
      >
        <div class="voice-card-header">
          <div class="voice-avatar" style="background-color:${voice.color || '#6366f1'}">
            ${voice.image
              ? html`<img src="${voice.image}" alt="${voice.name}" class="voice-avatar-img" />`
              : html`<span>${(voice.name || '?')[0]}</span>`
            }
          </div>
          <div class="voice-card-info">
            <div class="voice-card-name">${voice.name}</div>
            <div class="voice-card-meta">
              ${voice.accent ? html`<span>${voice.accent}</span>` : ''}
              ${voice.gender !== 'unknown' ? html`<span class="voice-card-gender">${voice.gender}</span>` : ''}
            </div>
          </div>
          <button
            class="voice-play-btn ${isPlaying ? 'playing' : ''}"
            @click=${(e) => { e.stopPropagation(); this._togglePlay(voice); }}
            title="${isLoading ? 'Loading preview...' : (isPlaying ? 'Stop preview' : 'Play preview')}"
            ?disabled=${isLoading}
          >
            <i class="fa-sharp fa-solid ${isLoading ? 'fa-spinner fa-spin' : (isPlaying ? 'fa-stop' : 'fa-play')}"></i>
          </button>
        </div>
        <div class="voice-card-id">${voice.canonical_name}</div>
        ${(voice.characteristics || []).length > 0 ? html`
          <div class="voice-tags">
            ${voice.characteristics.slice(0, 4).map(tag => html`<span class="voice-tag">${tag}</span>`)}
          </div>
        ` : ''}
        ${isSelected ? html`<div class="voice-selected-badge"><i class="fa-sharp fa-solid fa-check"></i></div>` : ''}
      </div>
    `;
  }

  _renderVoiceSkeleton() {
    return html`
      <div class="voice-grid">
        ${[1,2,3,4,5,6].map(() => html`
          <div class="voice-card sk-card">
            <div class="voice-card-header">
              <div class="voice-avatar skeleton-shimmer" style="background:var(--color-bg-inset);"></div>
              <div class="voice-card-info">
                <div class="skeleton-shimmer" style="width:80px;height:16px;border-radius:var(--radius-sm);"></div>
                <div class="skeleton-shimmer" style="width:60px;height:12px;border-radius:var(--radius-sm);margin-top:4px;"></div>
              </div>
            </div>
            <div class="skeleton-shimmer" style="width:140px;height:11px;border-radius:var(--radius-sm);margin-top:var(--space-2);"></div>
            <div class="voice-tags">
              <div class="skeleton-shimmer" style="width:50px;height:18px;border-radius:var(--radius-full);"></div>
              <div class="skeleton-shimmer" style="width:40px;height:18px;border-radius:var(--radius-full);"></div>
            </div>
          </div>
        `)}
      </div>
    `;
  }
}

customElements.define('agent-voice-tab', AgentVoiceTab);
